/**
 * /app/{slug}/directory — employee directory + org chart for everyone
 * (persona round wave B). Before, only HR could see the people list and
 * the org chart. This page is read-only and shows what GET /directory
 * returns to any member: name, designation, department, place of work,
 * manager — and work email / phone only when the company allows it (HR
 * sets that here). Never salary or personal details. HR keeps the full
 * People area under Run.
 */

import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Mail, MapPin, Phone, Search, Users } from "lucide-react";
import { toast } from "sonner";
import { toApiError } from "@/lib/api";
import { updateDirectorySettings } from "@/lib/api/directory";
import { directoryKeys, useDirectory } from "@/hooks/use-directory";
import { useDepartments } from "@/hooks/use-departments";
import { useAuth } from "@/lib/auth";
import { isHRAdmin } from "@/lib/nav";
import { departmentsOf, filterDirectory, reportsToLine } from "@/lib/employee-directory";
import { PageHeader } from "@/components/shared/page-header";
import { EmptyState } from "@/components/shared/empty-state";
import { OrgChartSimple } from "@/components/org-chart/org-chart-simple";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export const Route = createFileRoute("/_authgated/app/$tenantSlug/directory")({
  component: DirectoryRoute,
});

const ALL = "__all__";

function DirectoryRoute() {
  const { user } = useAuth();
  const dir = useDirectory();
  const [query, setQuery] = useState("");
  const [dept, setDept] = useState(ALL);
  // The Departments list (migration 169): active departments with people in
  // them; derived from the roster until it loads.
  const deptList = useDepartments();
  const departments = useMemo(
    () =>
      deptList.data
        ? deptList.data.departments.filter((d) => d.people_count > 0).map((d) => d.name)
        : departmentsOf(dir.entries),
    [deptList.data, dir.entries],
  );
  const people = useMemo(
    () => filterDirectory(dir.entries, query, dept === ALL ? "" : dept),
    [dir.entries, query, dept],
  );

  return (
    <>
      <PageHeader
        title="Directory"
        description="Find a colleague — their role, team, place of work and who they report to."
      />
      {isHRAdmin(user?.role) && dir.contactVisibility && (
        <VisibilitySettings email={dir.contactVisibility.email} phone={dir.contactVisibility.phone} />
      )}
      {dir.isLoading && (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-28 w-full" />
          ))}
        </div>
      )}
      {dir.isError && (
        <Alert variant="destructive">
          <AlertTitle>Could not load the directory</AlertTitle>
          <AlertDescription>{toApiError(dir.error).message}</AlertDescription>
        </Alert>
      )}
      {dir.hasDirectory && (
        <Tabs defaultValue="people">
          <TabsList>
            <TabsTrigger value="people">People</TabsTrigger>
            <TabsTrigger value="org">Org Chart</TabsTrigger>
          </TabsList>
          <TabsContent value="people" className="mt-4 space-y-4">
            <div className="flex flex-wrap items-end gap-3">
              <div className="relative w-full sm:w-72">
                <Search aria-hidden className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  aria-label="Search people"
                  placeholder="Search by name, role, team or place"
                  className="pl-8"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                />
              </div>
              {departments.length > 0 && (
                <Select value={dept} onValueChange={setDept}>
                  <SelectTrigger aria-label="Department" className="w-full sm:w-52">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ALL}>All Departments</SelectItem>
                    {departments.map((d) => (
                      <SelectItem key={d} value={d}>
                        {d}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
            {people.length === 0 ? (
              <EmptyState icon={Users} title="No one matches" description="Try a different name or team." />
            ) : (
              <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="employee-directory">
                {people.map((p) => (
                  <li key={p.id} className="space-y-1 rounded-lg border bg-card p-4 text-sm">
                    <p className="font-medium">
                      {p.display_name || `${p.first_name} ${p.last_name}`}
                      {p.id === user?.user_id && <span className="text-muted-foreground"> (you)</span>}
                    </p>
                    <p className="text-muted-foreground">
                      {[p.designation, p.department].filter(Boolean).join(" · ") || "—"}
                    </p>
                    {reportsToLine(p, dir.byId) && <p className="text-muted-foreground">{reportsToLine(p, dir.byId)}</p>}
                    {p.location && (
                      <p className="flex items-center gap-1.5 text-muted-foreground">
                        <MapPin aria-hidden className="h-3.5 w-3.5" />
                        {p.location}
                      </p>
                    )}
                    {p.email && (
                      <p className="flex items-center gap-1.5 break-all">
                        <Mail aria-hidden className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                        <a href={`mailto:${p.email}`} className="inline-flex items-center hover:underline pointer-coarse:min-h-11">
                          {p.email}
                        </a>
                      </p>
                    )}
                    {p.phone && (
                      <p className="flex items-center gap-1.5">
                        <Phone aria-hidden className="h-3.5 w-3.5 text-muted-foreground" />
                        <a href={`tel:${p.phone}`} className="inline-flex items-center hover:underline pointer-coarse:min-h-11">
                          {p.phone}
                        </a>
                      </p>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </TabsContent>
          <TabsContent value="org" className="mt-4">
            <OrgChartSimple entries={dir.entries} myId={user?.user_id} />
          </TabsContent>
        </Tabs>
      )}
    </>
  );
}

function VisibilitySettings({ email, phone }: Readonly<{ email: boolean; phone: boolean }>) {
  const qc = useQueryClient();
  const save = useMutation({
    mutationFn: updateDirectorySettings,
    onSuccess: () => qc.invalidateQueries({ queryKey: directoryKeys.all }),
  });

  async function set(body: { show_email?: boolean; show_phone?: boolean }) {
    try {
      await save.mutateAsync(body);
      toast.success("Directory settings saved.");
    } catch (err) {
      toast.error(toApiError(err).message);
    }
  }

  return (
    <Card className="mb-4">
      <CardHeader className="pb-2">
        <CardTitle className="text-base">What colleagues can see</CardTitle>
        <CardDescription>
          Everyone sees name, role, team, place of work and manager. Choose whether
          they also see each other&apos;s work email and phone. Only HR sees this setting.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-wrap gap-6 text-sm">
        <div className="relative flex items-center gap-2 pointer-coarse:min-h-11 pointer-coarse:-mx-3.5 pointer-coarse:px-3.5">
          <Switch id="dir-email" checked={email} disabled={save.isPending} onCheckedChange={(v) => void set({ show_email: v })} />
          <Label htmlFor="dir-email" className="font-normal after:absolute after:inset-0 after:content-['']">Show Work Email</Label>
        </div>
        <div className="relative flex items-center gap-2 pointer-coarse:min-h-11 pointer-coarse:-mx-3.5 pointer-coarse:px-3.5">
          <Switch id="dir-phone" checked={phone} disabled={save.isPending} onCheckedChange={(v) => void set({ show_phone: v })} />
          <Label htmlFor="dir-phone" className="font-normal after:absolute after:inset-0 after:content-['']">Show Phone Number</Label>
        </div>
      </CardContent>
    </Card>
  );
}
